import axios from "axios";

import API_ROUTES from "./constants";
import { getAuthToken, unwrapApiList } from "./apiResponse";
import { normalizePetOwner } from "./domainAdapters";

const readStoredJson = (key) => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch (error) {
    return {};
  }
};

export const getStoredSessionUser = (override = null) => ({
  ...readStoredJson("loginData"),
  ...readStoredJson("user"),
  ...(override || {})
});

export const persistSessionUser = (patch = {}) => {
  if (typeof window === "undefined") {
    return patch;
  }

  const mergedUser = {
    ...getStoredSessionUser(),
    ...patch
  };

  localStorage.setItem("user", JSON.stringify(mergedUser));
  localStorage.setItem("loginData", JSON.stringify(mergedUser));

  const profileId = mergedUser.profile_id || mergedUser.id;
  if (profileId) {
    localStorage.setItem("profile_id", String(profileId));
  }

  if (mergedUser.hospital_id) {
    localStorage.setItem("hospital_id", String(mergedUser.hospital_id));
  }

  if (mergedUser.doctor_id) {
    localStorage.setItem("doctor_id", String(mergedUser.doctor_id));
  }

  const ownerId = mergedUser.pet_owner_id || mergedUser.petOwnerId;
  if (ownerId) {
    localStorage.setItem("ownerId", String(ownerId));
    localStorage.setItem("petOwnerId", String(ownerId));
    localStorage.setItem("pet_owner_id", String(ownerId));
  }

  return mergedUser;
};

export const getCurrentProfileId = (userData = null) => {
  const currentUser = getStoredSessionUser(userData);
  const profileId =
    currentUser.profile_id ||
    currentUser.id ||
    (typeof window !== "undefined" ? localStorage.getItem("profile_id") : null);

  return profileId ? Number(profileId) : null;
};

export const getStoredDoctorId = () => {
  const currentUser = getStoredSessionUser();
  const doctorId = currentUser.doctor_id || (typeof window !== "undefined" ? localStorage.getItem("doctor_id") : null);

  return doctorId ? Number(doctorId) : null;
};

export const getStoredPetOwnerId = () => {
  const currentUser = getStoredSessionUser();
  const ownerId =
    currentUser.pet_owner_id ||
    currentUser.petOwnerId ||
    (typeof window !== "undefined"
      ? localStorage.getItem("pet_owner_id") ||
        localStorage.getItem("petOwnerId") ||
        localStorage.getItem("ownerId")
      : null);

  return ownerId ? Number(ownerId) : null;
};

export const resolveCurrentDoctor = async ({ token, userData } = {}) => {
  const authToken = token || getAuthToken();
  const profileId = getCurrentProfileId(userData);

  if (!authToken || !profileId) {
    return null;
  }

  const response = await axios.get(`${API_ROUTES.HOSPITAL.DOCTORS}?profile_id=${profileId}&pageSize=1`, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });

  const doctor = unwrapApiList(response)[0] || null;

  if (!doctor?.id) {
    return null;
  }

  persistSessionUser({
    doctor_id: doctor.id,
    hospital_id: doctor.hospital_id || getStoredSessionUser().hospital_id
  });

  return doctor;
};

export const resolveCurrentPetOwner = async ({ token, userData } = {}) => {
  const authToken = token || getAuthToken();
  const profileId = getCurrentProfileId(userData);

  if (!authToken || !profileId) {
    return null;
  }

  const response = await axios.get(API_ROUTES.PET_OWNER.CURRENT(profileId), {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });

  const owner = unwrapApiList(response).map(normalizePetOwner)[0] || null;

  if (!owner?.id) {
    return null;
  }

  persistSessionUser({
    pet_owner_id: owner.id,
    petOwnerId: owner.id,
    hospital_id: owner.hospital_id || getStoredSessionUser().hospital_id
  });

  return owner;
};
